import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Hash, Globe, Phone, Mail, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { COMPANY_DETAILS, COMPANY_STATEMENT } from '../data/company';

const ROWS = [
  { icon: Building2, label: 'Legal entity',        value: COMPANY_DETAILS.legalName },
  { icon: Hash,      label: 'Company Registration No.', value: COMPANY_DETAILS.registrationNo },
  { icon: MapPin,    label: 'Registered office',   value: COMPANY_DETAILS.registeredOffice },
  { icon: Globe,     label: 'Vietnam operations',  value: COMPANY_DETAILS.vietnamOperations },
];

const CompanyDetailsPage = () => (
  <div className="min-h-screen bg-gray-50">
    <Helmet>
      <title>Company Details | VietClinics — Viet Medica Ltd</title>
      <meta name="description" content="Company details for VietClinics, the trading name of Viet Medica Ltd — a private company limited by shares registered in Mauritius, with operations in Ho Chi Minh City, Vietnam." />
      <link rel="canonical" href="https://vietclinics.com/company-details" />
    </Helmet>
    <Header />

    <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-12">
      <div className="container mx-auto px-4 text-center">
        <Building2 className="h-12 w-12 text-white mx-auto mb-4" />
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Company Details</h1>
        <p className="text-white/80">Legal and registration information for VietClinics</p>
      </div>
    </div>

    <div className="container mx-auto px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-6">
        <section className="bg-white rounded-2xl shadow-sm p-6 md:p-10" data-testid="company-details-card">
          <p className="text-gray-800 text-base leading-relaxed font-medium" data-testid="company-statement">
            {COMPANY_STATEMENT}
          </p>
          <dl className="mt-8 divide-y divide-gray-100">
            {ROWS.map(({ icon: Icon, label, value }) => (
              <div key={label} className="py-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
                <dt className="sm:w-56 flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-500">
                  <Icon className="h-4 w-4 text-[#1FA7A1]" /> {label}
                </dt>
                <dd className="text-gray-900 font-medium" data-testid={`company-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="bg-white rounded-2xl shadow-sm p-6 md:p-10" data-testid="company-contact-card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Contact</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {COMPANY_DETAILS.phones.map((p) => (
              <a key={p} href={`tel:${p.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-gray-700 hover:text-[#1FA7A1] transition-colors">
                <Phone className="h-4 w-4 text-[#1FA7A1]" /> {p}
              </a>
            ))}
            {COMPANY_DETAILS.emails.map((e) => (
              <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 text-gray-700 hover:text-[#1FA7A1] transition-colors">
                <Mail className="h-4 w-4 text-[#1FA7A1]" /> {e}
              </a>
            ))}
          </div>
        </section>

        <section className="bg-[#F0FAF8] border border-[#DCEFEC] rounded-2xl p-6 text-sm text-slate-700 leading-relaxed" data-testid="company-platform-note">
          VietClinics is an independent healthcare coordination platform. We are not a hospital, clinic or
          medical provider — diagnosis, medical advice, treatment plans and pricing are provided directly by
          licensed clinics and doctors.
          <div className="flex flex-wrap gap-4 mt-4">
            <Link to="/about" className="inline-flex items-center gap-1 font-bold text-[#1FA7A1]" data-testid="company-link-about">About Us <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/terms" className="inline-flex items-center gap-1 font-bold text-[#1FA7A1]" data-testid="company-link-terms">Terms &amp; Conditions <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/privacy" className="inline-flex items-center gap-1 font-bold text-[#1FA7A1]" data-testid="company-link-privacy">Privacy Policy <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </section>
      </div>
    </div>

    <Footer />
  </div>
);

export default CompanyDetailsPage;
